import React, { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, Search } from 'lucide-react';
import { useUIColors } from '../../../../hooks/useTheme';

const money = (value) =>
  new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const Stacked = ({ top, bottom, topColor, muted }) => (
  <div style={{ lineHeight: 1.25 }}>
    <div style={{ fontWeight: 700, fontSize: 12, color: topColor }}>{top || '—'}</div>
    {bottom ? <div style={{ fontSize: 11, color: muted, marginTop: 1 }}>{bottom}</div> : null}
  </div>
);

const chartLeaf = (row) => {
  for (const key of [
    'detail_level_6',
    'detail_level_5',
    'detail_level_4',
    'detail_level_3',
    'detail_level_2',
    'detail_level_1',
  ]) {
    const value = String(row?.[key] || '').trim();
    if (value) return value;
  }
  return row?.account_code || 'Cuenta';
};

const TABLE_COLUMNS = [
  { key: 'Fecha', align: 'left', sortKey: 'entry_date' },
  { key: 'Cuenta', align: 'left', sortKey: 'account' },
  { key: 'Asiento', align: 'left', sortKey: 'entry_code' },
  { key: 'Glosa', align: 'left', sortKey: 'glosa' },
  { key: 'DEBE', align: 'right', sortKey: 'debe' },
  { key: 'HABER', align: 'right', sortKey: 'haber' },
];

const getSortValue = (row, sortKey) => {
  switch (sortKey) {
    case 'entry_date':
      return String(row.entry_date || '');
    case 'account':
      return `${row.account_code_snapshot || ''} ${row.account_label_snapshot || ''}`.toLowerCase();
    case 'entry_code':
      return String(row.entry_code || '').toLowerCase();
    case 'glosa':
      return String(row.line_description || row.entry_description || '').toLowerCase();
    case 'debe':
      return row.side === 'debe' ? Number(row.amount) || 0 : 0;
    case 'haber':
      return row.side === 'haber' ? Number(row.amount) || 0 : 0;
    default:
      return '';
  }
};

const compareRows = (a, b, sortKey, sortDir) => {
  const mult = sortDir === 'asc' ? 1 : -1;
  const va = getSortValue(a, sortKey);
  const vb = getSortValue(b, sortKey);
  if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * mult;
  return String(va).localeCompare(String(vb), 'es', { numeric: true, sensitivity: 'base' }) * mult;
};

/**
 * Libro mayor: mismo look que Table3 (filas planas con filtros).
 */
const AccountingLedgerTable3 = ({
  rows = [],
  accounts = [],
  loading = false,
  error = '',
  tableTitle = 'Libro mayor',
  subtitleOverride,
  accountId = '',
  dateFrom = '',
  dateTo = '',
  onAccountChange,
  onDateFromChange,
  onDateToChange,
  periodLocked = false,
}) => {
  const ui = useUIColors();
  const bg = ui.page;
  const headerBg = ui.header;
  const border = ui.border;
  const text = ui.text;
  const muted = ui.muted;
  const rowHover = ui.rowHover;
  const inputBg = ui.inputBg || ui.panel || bg;
  const inputBorder = ui.inputBorder || border;
  const padV = 9;
  const padH = 10;
  const thPadV = 8;

  const [searchQuery, setSearchQuery] = useState('');
  const [sortColumn, setSortColumn] = useState('entry_date');
  const [sortDir, setSortDir] = useState('asc');

  const selectStyle = {
    height: 36,
    padding: '0 10px',
    borderRadius: 8,
    border: `1px solid ${inputBorder}`,
    background: inputBg,
    color: text,
    fontSize: 13,
    outline: 'none',
    boxSizing: 'border-box',
  };

  const filteredRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => {
      const haystack = [
        row.entry_date,
        row.entry_code,
        row.account_code_snapshot,
        row.account_label_snapshot,
        row.line_description,
        row.entry_description,
        row.side,
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [rows, searchQuery]);

  const sortedRows = useMemo(() => {
    const copy = [...filteredRows];
    copy.sort((a, b) => compareRows(a, b, sortColumn, sortDir));
    return copy;
  }, [filteredRows, sortColumn, sortDir]);

  const totals = useMemo(
    () =>
      filteredRows.reduce(
        (acc, row) => {
          if (row.side === 'debe') acc.debe += Number(row.amount || 0);
          else acc.haber += Number(row.amount || 0);
          return acc;
        },
        { debe: 0, haber: 0 }
      ),
    [filteredRows]
  );

  const handleSortClick = (sortKey) => {
    if (sortColumn === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortColumn(sortKey);
      setSortDir(sortKey === 'entry_date' ? 'asc' : 'desc');
    }
  };

  const SortIcon = ({ columnKey }) => {
    if (sortColumn !== columnKey) return <ArrowUpDown size={12} color={muted} />;
    return sortDir === 'asc' ? <ArrowUp size={12} color={text} /> : <ArrowDown size={12} color={text} />;
  };

  const clearFilters = () => {
    setSearchQuery('');
    onAccountChange?.('');
    if (!periodLocked) {
      onDateFromChange?.('');
      onDateToChange?.('');
    }
  };

  const hasActiveFilters =
    Boolean(searchQuery.trim()) ||
    Boolean(accountId) ||
    (!periodLocked && (Boolean(dateFrom) || Boolean(dateTo)));

  const subtitle =
    subtitleOverride ||
    (loading
      ? 'Cargando datos...'
      : error ||
        `${filteredRows.length} movimiento${filteredRows.length === 1 ? '' : 's'} · DEBE ${money(
          totals.debe
        )} · HABER ${money(totals.haber)}`);

  return (
    <div
      style={{
        border: `1px solid ${border}`,
        borderRadius: 12,
        overflow: 'hidden',
        background: bg,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 16px',
          borderBottom: `1px solid ${border}`,
          background: headerBg,
          flexWrap: 'wrap',
          gap: 10,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', minWidth: 0 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: text }}>{tableTitle}</h3>
          <p style={{ margin: 0, fontSize: 12, color: muted }}>{subtitle}</p>
        </div>
      </div>

      <div
        style={{
          padding: '10px 16px',
          borderBottom: `1px solid ${border}`,
          background: bg,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 10,
          alignItems: 'flex-end',
        }}
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: '1 1 200px' }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>
            Buscar
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Search size={16} color={muted} aria-hidden />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Cuenta, asiento, glosa…"
              style={{ ...selectStyle, flex: 1, minWidth: 0, cursor: 'text' }}
            />
          </span>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 220 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>
            Cuenta
          </span>
          <select
            value={accountId}
            onChange={(e) => onAccountChange?.(e.target.value)}
            style={selectStyle}
          >
            <option value="">Todas las cuentas</option>
            {accounts.map((row) => (
              <option key={row.id} value={row.id}>
                {row.account_code} · {chartLeaf(row)}
              </option>
            ))}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 140 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>
            Desde{periodLocked ? ' (período)' : ''}
          </span>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => onDateFromChange?.(e.target.value)}
            disabled={periodLocked || !onDateFromChange}
            style={{
              ...selectStyle,
              opacity: periodLocked || !onDateFromChange ? 0.75 : 1,
              cursor: periodLocked || !onDateFromChange ? 'default' : undefined,
            }}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 140 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>
            Hasta{periodLocked ? ' (período)' : ''}
          </span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => onDateToChange?.(e.target.value)}
            disabled={periodLocked || !onDateToChange}
            style={{
              ...selectStyle,
              opacity: periodLocked || !onDateToChange ? 0.75 : 1,
              cursor: periodLocked || !onDateToChange ? 'default' : undefined,
            }}
          />
        </label>
        {hasActiveFilters ? (
          <button
            type="button"
            onClick={clearFilters}
            style={{
              height: 36,
              padding: '0 12px',
              borderRadius: 8,
              border: `1px solid ${inputBorder}`,
              background: inputBg,
              color: text,
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            Limpiar
          </button>
        ) : null}
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 860 }}>
          <thead>
            <tr style={{ background: headerBg, borderBottom: `1px solid ${border}` }}>
              {TABLE_COLUMNS.map((col) => (
                <th
                  key={col.key}
                  style={{
                    padding: `${thPadV}px ${padH}px`,
                    textAlign: col.align,
                    fontSize: 11,
                    fontWeight: 700,
                    color: muted,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                  onClick={() => handleSortClick(col.sortKey)}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    {col.key}
                    <SortIcon columnKey={col.sortKey} />
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={TABLE_COLUMNS.length}
                  style={{ padding: 24, textAlign: 'center', color: muted, fontSize: 13 }}
                >
                  Cargando mayor…
                </td>
              </tr>
            ) : sortedRows.length === 0 ? (
              <tr>
                <td
                  colSpan={TABLE_COLUMNS.length}
                  style={{ padding: 24, textAlign: 'center', color: muted, fontSize: 13 }}
                >
                  {error ||
                    'No hay movimientos contabilizados. Crea asientos y márcalos como Contabilizado.'}
                </td>
              </tr>
            ) : (
              sortedRows.map((row, idx) => (
                <tr
                  key={`${row.entry_code}-${row.account_code_snapshot}-${idx}`}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = rowHover || headerBg;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = bg;
                  }}
                  style={{ borderBottom: `1px solid ${border}`, background: bg }}
                >
                  <td style={{ padding: `${padV}px ${padH}px`, fontSize: 12, color: text }}>
                    {String(row.entry_date || '').slice(0, 10)}
                  </td>
                  <td style={{ padding: `${padV}px ${padH}px` }}>
                    <Stacked
                      top={row.account_code_snapshot || '—'}
                      bottom={row.account_label_snapshot || ''}
                      topColor={text}
                      muted={muted}
                    />
                  </td>
                  <td style={{ padding: `${padV}px ${padH}px`, fontSize: 12, color: text }}>
                    {row.entry_code || '—'}
                  </td>
                  <td style={{ padding: `${padV}px ${padH}px`, fontSize: 12, color: muted }}>
                    {row.line_description || row.entry_description || '—'}
                  </td>
                  <td
                    style={{
                      padding: `${padV}px ${padH}px`,
                      textAlign: 'right',
                      fontSize: 12,
                      color: '#2563eb',
                      fontWeight: 600,
                    }}
                  >
                    {row.side === 'debe' ? money(row.amount) : '—'}
                  </td>
                  <td
                    style={{
                      padding: `${padV}px ${padH}px`,
                      textAlign: 'right',
                      fontSize: 12,
                      color: '#059669',
                      fontWeight: 600,
                    }}
                  >
                    {row.side === 'haber' ? money(row.amount) : '—'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AccountingLedgerTable3;
